import { CheckBoxFilterBase, GridComponent } from '@syncfusion/ej2-react-grids';
import { useQueryClient } from '@tanstack/react-query';
import { Dispatch, MutableRefObject, RefObject, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useIsFeatureEnabled } from '../api/features/features';
import { getGetAmountsEntriesQueryKey, useGetAmountsEntries, useGetAmountsEntryOptions, useSaveAmounts } from '../api/report/report';
import { IEnterAmountsDialogProps } from '../components/enter-amounts-dialog/EnterAmountsDialog';
import { IJournalEntryGridProps } from '../components/sidebar/IJournalEntryGridProps';
import { IJournalEntryPendingArgs } from '../components/sidebar/IJournalEntryPendingArgs';
import { AllowNull } from '../data-types/AllowUndefinedAndNull';
import {
    AmountDataEntryOptions,
    GetAmountsEntriesParams,
    IAccountAmountRow,
    IAdjustment,
    ICalendarPeriod,
    ICase,
    IEntity,
    IJurisdiction,
    IWhitePaperReportChanges,
    ProblemDetails,
    SaveAmountsRequest,
    SaveAmountsResponse,
} from '../model';
import { EnterAmountsDialogFiltersAction, enterAmountsDialogFiltersReducer } from '../reducers/enterAmountsDialogFiltersReducer';
import {
    ConfirmationDialogType,
    EnterAmountsDialogReducer,
    EnterAmountsDialogReducerAction,
    enterAmountsDialogStateReducer,
    EnterAmountsModalActionActionType,
    IEnterAmountsDialogState,
} from '../reducers/enterAmountsDialogReducer';
import { getSyncfusionPopupMenuDismissHack, ISyncfusionGridPopupMenuDismissHack } from '../utils/DialogUtilsV9';
import { FilteredEnterAmountsEnabled } from '../utils/FeatureFlags.constants';
import { resolveEnterAmountsDefaultJurisdiction } from '../utils/resolveEnterAmountsDefaultJurisdiction';
import { ErrorType } from '../utils/UseCustomInstance';

export interface IUseEnterAmountsDataHook {
    accountAmountRows: IAccountAmountRow[] | undefined;
    cases: ICase[] | undefined;
    currentlyDisplayedCase: ICase | undefined;
    datasetCase: ICase | undefined;
    entities: IEntity[] | undefined;
    currentlyDisplayedEntity: IEntity | undefined;
    datasetEntity: IEntity | undefined;
    jurisdictions: IJurisdiction[] | undefined;
    currentlyDisplayedJurisdiction: IJurisdiction | undefined;
    datasetJurisdiction: IJurisdiction | undefined;
    years: ICalendarPeriod[] | undefined;
    currentlyDisplayedYear: ICalendarPeriod | undefined;
    datasetYear: ICalendarPeriod | undefined;
    adjustments: IAdjustment[] | undefined;
    currentlyDisplayedAdjustment: IAdjustment | undefined;
    datasetAdjustment: IAdjustment | undefined;
    saveAmounts: (
        request: { data: SaveAmountsRequest },
        options: {
            onSettled: (
                data: SaveAmountsResponse | undefined,
                error: ErrorType<ProblemDetails> | null,
                variables: { data: SaveAmountsRequest },
                context: unknown | undefined
            ) => void;
            onSuccess: (
                data: SaveAmountsResponse | undefined,
                variables: { data: SaveAmountsRequest },
                context: unknown | undefined
            ) => void;
        }
    ) => void;
    updateDialogState: Dispatch<EnterAmountsDialogReducerAction>;
    isLoading: boolean;
    saveButtonIsDisabled: boolean;
    applyButtonIsDisabled: boolean;
    changeTrackingData: IWhitePaperReportChanges;
    onFilterUpdate: (filterSelections: IEnterAmountsDialogFiltersUIState) => void;
    onFilterApply: () => void;
    onFilterReset: () => void;
    amountsOnly: boolean;
    combineDebitCreditColumns: boolean;
    showAllAccounts: boolean;
    loadStateChangeEvent: 'Initialize' | 'FilterChange' | 'SaveAmounts' | null;
    dispatchFiltersUIState: (action: EnterAmountsDialogFiltersAction) => void;
    journalEntryGridProps: IJournalEntryGridProps | null;
    journalEntryGridRef: RefObject<GridComponent>;
    journalEntryGridPopupMenuDismissHack: ISyncfusionGridPopupMenuDismissHack;
    currentFilterSelections: IEnterAmountsDialogDatasetFilterSelections | null;
    datasetFilterSelections: IEnterAmountsDialogDatasetFilterSelections;
    showConfirmationDialog: boolean;
    confirmationDialogType: ConfirmationDialogType;
    isFilterReset: boolean;
    isDirty: boolean;
    refetchAmounts: () => void;
    onDiscardConfirmation: () => void;
    isFilteredEnterAmountsEnabled: boolean;
    pendingJournalEntryArgs: IJournalEntryPendingArgs | null;
}
const findIEntityByCode = (entities: IEntity[] | null | undefined, entityCode: string | undefined): IEntity | undefined => {
    return entities?.find((entity: IEntity) => entity.code === entityCode);
};
const findICaseByCode = (cases: ICase[] | null | undefined, caseCode: string | undefined): ICase | undefined => {
    // This matches SMART behavior, when Return Case is not found in the list of cases, it always defaults to the first item
    return cases?.find((ICase: ICase) => ICase.code === caseCode) ?? cases?.[0];
};
const findICalendarPeriodByCode = (
    years: ICalendarPeriod[] | null | undefined,
    periodCode: string | undefined
): ICalendarPeriod | undefined => {
    if (years && years.length === 1) {
        return years[0];
    }

    return years?.find((calendarPeriodDto: ICalendarPeriod) => calendarPeriodDto.periodYear?.toString() === periodCode);
};
const findIAdjustmentByCode = (
    adjustments: IAdjustment[] | null | undefined,
    adjustmentCode: string | undefined
): IAdjustment | undefined => {
    return adjustments?.find((adjustmentDto: IAdjustment) => adjustmentDto.adjustmentCode === adjustmentCode);
};

export const useEnterAmountsData: (props: IEnterAmountsDialogProps) => IUseEnterAmountsDataHook = (props: IEnterAmountsDialogProps) => {
    const journalEntryGridRef: RefObject<GridComponent> = useRef<GridComponent>(null);
    const journalEntryGridFilterMenuRef: MutableRefObject<AllowNull<CheckBoxFilterBase>> = useRef(null);
    const currentLoadEvent: MutableRefObject<'Initialize' | 'FilterChange' | 'SaveAmounts' | null> = useRef(null);
    const { data: amountsDataEntryOptions, isFetching: isAmountsDataEntryOptionsFetching } =
        useGetAmountsEntryOptions<AmountDataEntryOptions>({
            ColumnId: props.columnId,
            RowNumber: props.definingRowNumber ?? props.drillDownRowNumber,
            TaxReturnKey: props.taxReturnKey,
        });
    const queryClient = useQueryClient();
    const { cases, entities, jurisdictions, years, adjustments, accounts } = { ...amountsDataEntryOptions };
    const defaultJurisdiction: IJurisdiction | undefined = resolveEnterAmountsDefaultJurisdiction(jurisdictions, props.jurisdiction);
    const initialFilterState: IEnterAmountsDialogDatasetFilterSelections = {
        jurisdiction: defaultJurisdiction,
        calendarPeriod: findICalendarPeriodByCode(years, props.period),
        entity: findIEntityByCode(entities, props.entity),
        adjustment: findIAdjustmentByCode(adjustments, props.adjustmentCode),
        case: findICaseByCode(cases, props.case),
    };
    const { data: isFilteredEnterAmountsEnabled, isFetching: isFilteredEnterAmountsEnabledFetching } = useIsFeatureEnabled({
        featureFlagName: FilteredEnterAmountsEnabled,
    });
    const initialState: IEnterAmountsDialogState = {
        location: props.location,
        show: false,
        changeTrackingData: props.changeTrackingData,
        saveButtonIsDisabled: true,
        applyButtonDisabled: true,
        rowDescription: props.rowDescription,
        amountsOnly: false,
        combineDebitCreditColumns: false,
        showAllAccounts: !props.isInputRow,
        journalEntryGridProps: null,
        currentFilterSelections: initialFilterState,
        datasetFilterSelections: initialFilterState,
        showConfirmationDialog: false,
        confirmationDialogType: null,
        isDirty: false,
        isFilterReset: false,
        pendingJournalEntryArgs: null,
    };
    const [dialogState, updateDialogState] = useReducer<EnterAmountsDialogReducer>(enterAmountsDialogStateReducer, initialState);
    const amountEntriesRequest: GetAmountsEntriesParams = {
        CaseCode: dialogState.datasetFilterSelections.case ? dialogState.datasetFilterSelections.case.code! : props.case,
        //location: props.location,
        EntityCode: dialogState.datasetFilterSelections.entity ? dialogState.datasetFilterSelections.entity.code! : props.entity,
        Period: dialogState.datasetFilterSelections.calendarPeriod
            ? dialogState.datasetFilterSelections.calendarPeriod.periodYear!.toString()
            : props.period,
        AdjustmentCode: dialogState.datasetFilterSelections.adjustment
            ? dialogState.datasetFilterSelections.adjustment.adjustmentCode!
            : props.adjustmentCode,
        Jurisdiction: dialogState.datasetFilterSelections.jurisdiction
            ? dialogState.datasetFilterSelections.jurisdiction.code!
            : props.jurisdiction,
        // TODO: add account codes to this request, get the list of account codes from glenn's API (getAmountsEntryOptions)
        // or maybe this can just be done in BFF?
    };

    const onFilterUpdate = useCallback(
        (filterSelections: Partial<IEnterAmountsDialogState>): void => {
            updateDialogState({ type: EnterAmountsModalActionActionType.FiltersUpdated, filterSelections });
        },
        [updateDialogState]
    );

    const onFilterReset = useCallback(() => {
        if (!dialogState.isDirty) {
            onFilterUpdate({
                amountsOnly: false,
                applyButtonDisabled: true,
                combineDebitCreditColumns: false,
                showAllAccounts: !props.isInputRow,
                datasetFilterSelections: {
                    adjustment: findIAdjustmentByCode(adjustments, props.adjustmentCode),
                    calendarPeriod: findICalendarPeriodByCode(years, props.period),
                    case: findICaseByCode(cases, props.case),
                    entity: findIEntityByCode(entities, props.entity),
                    jurisdiction: defaultJurisdiction,
                },
                currentFilterSelections: {
                    adjustment: findIAdjustmentByCode(adjustments, props.adjustmentCode),
                    calendarPeriod: findICalendarPeriodByCode(years, props.period),
                    case: findICaseByCode(cases, props.case),
                    entity: findIEntityByCode(entities, props.entity),
                    jurisdiction: defaultJurisdiction,
                },
            });
            onFilterApply();
            queryClient.invalidateQueries({ queryKey: getGetAmountsEntriesQueryKey(amountEntriesRequest) });
            refetchAmountEntries();
        } else {
            updateDialogState({
                type: EnterAmountsModalActionActionType.ConfirmationDialogOpened,
                dialogType: ConfirmationDialogType.FilterReset,
            });
            updateDialogState({ type: EnterAmountsModalActionActionType.FiltersReset });
        }
    }, [
        updateDialogState,
        cases,
        entities,
        jurisdictions,
        years,
        adjustments,
        defaultJurisdiction,
        dialogState.isDirty,
        queryClient,
        amountEntriesRequest,
    ]);

    const dispatchFiltersUIState = useCallback(
        (action: EnterAmountsDialogFiltersAction) => {
            const reducerResult = enterAmountsDialogFiltersReducer({ ...dialogState }, action);
            onFilterUpdate({ ...dialogState, currentFilterSelections: reducerResult.currentFilterSelections! });
        },
        [updateDialogState, dialogState]
    );

    const onFilterApply: () => void = useCallback(() => {
        if (!dialogState.isDirty) {
            updateDialogState({ type: EnterAmountsModalActionActionType.FiltersApplied });
        } else {
            updateDialogState({
                type: EnterAmountsModalActionActionType.ConfirmationDialogOpened,
                dialogType: ConfirmationDialogType.FilterReset,
            });
        }
    }, [updateDialogState, dialogState.isDirty]);

    useEffect(() => {
        if (
            !dialogState.datasetFilterSelections.adjustment &&
            !dialogState.datasetFilterSelections.case &&
            !dialogState.datasetFilterSelections.entity &&
            !dialogState.datasetFilterSelections.jurisdiction &&
            !dialogState.datasetFilterSelections.calendarPeriod
        ) {
            const filterSelections = {
                jurisdiction: defaultJurisdiction,
                calendarPeriod: findICalendarPeriodByCode(years, props.period),
                entity: findIEntityByCode(entities, props.entity),
                adjustment: findIAdjustmentByCode(adjustments, props.adjustmentCode),
                case: findICaseByCode(cases, props.case),
            };
            onFilterUpdate({
                currentFilterSelections: filterSelections,
                datasetFilterSelections: filterSelections,
            });
        }
    }, [jurisdictions, years, entities, adjustments, cases, defaultJurisdiction, updateDialogState]);

    const isAmountEntriesQueryEnabled: boolean =
        !isAmountsDataEntryOptionsFetching && (!defaultJurisdiction || !!dialogState.datasetFilterSelections.jurisdiction);

    const {
        data: amountsEntriesResponse,
        isFetching: isAmountsEntriesFetching,
        isError: isAmountsEntriesError,
        refetch: refetchAmountEntries,
    } = useGetAmountsEntries(amountEntriesRequest, {
        query: {
            enabled: isAmountEntriesQueryEnabled,
        },
    });

    const filteredAccountAmountRows: IAccountAmountRow[] | null | undefined = useMemo(() => {
        if (isFilteredEnterAmountsEnabled && props.isInputRow && amountsEntriesResponse?.accounts && accounts && accounts.length > 0) {
            return amountsEntriesResponse.accounts.filter(
                (account) => account.accountCode && accounts.some((a) => a.accountCode === account.accountCode)
            );
        }
        return amountsEntriesResponse?.accounts;
    }, [amountsEntriesResponse?.accounts, accounts, props.isInputRow]);

    const { mutate: saveAmounts, isLoading: isSaveAmountsLoading } = useSaveAmounts();

    // Show loading until BOTH options and amount entries have finished (no flash between the two)
    const isWaitingForOptions = isAmountsDataEntryOptionsFetching || !amountsDataEntryOptions;
    const isWaitingForFilterStateSync =
        !!amountsDataEntryOptions && !!defaultJurisdiction && !dialogState.datasetFilterSelections.jurisdiction;
    const isWaitingForAmountEntries =
        isAmountEntriesQueryEnabled && (isAmountsEntriesFetching || (amountsEntriesResponse === undefined && !isAmountsEntriesError));

    const isLoading: boolean =
        isWaitingForOptions ||
        isWaitingForFilterStateSync ||
        isWaitingForAmountEntries ||
        isSaveAmountsLoading ||
        isFilteredEnterAmountsEnabledFetching;
    let loadStateChangeEvent: 'Initialize' | 'FilterChange' | 'SaveAmounts' | null = null;

    if (isLoading) {
        if (!currentLoadEvent.current) {
            if (isAmountsDataEntryOptionsFetching) {
                loadStateChangeEvent = 'Initialize';
            } else if (isSaveAmountsLoading) {
                loadStateChangeEvent = 'SaveAmounts';
            } else if (isAmountsEntriesFetching) {
                loadStateChangeEvent = 'FilterChange';
            }

            currentLoadEvent.current = loadStateChangeEvent;
        }
    } else {
        if (currentLoadEvent.current) {
            loadStateChangeEvent = currentLoadEvent.current;
            currentLoadEvent.current = null;
        }
    }
    const datasetEntity: IEntity | undefined = dialogState.datasetFilterSelections.entity ?? findIEntityByCode(entities, props.entity);
    const datasetJurisdiction: IJurisdiction | undefined = dialogState.datasetFilterSelections.jurisdiction ?? defaultJurisdiction;
    const datasetCase: ICase | undefined = dialogState.datasetFilterSelections.case ?? findICaseByCode(cases, props.case);
    const datasetYear: ICalendarPeriod | undefined =
        dialogState.datasetFilterSelections.calendarPeriod ?? findICalendarPeriodByCode(years, props.period);
    const datasetAdjustment: IAdjustment | undefined =
        dialogState.datasetFilterSelections.adjustment ?? findIAdjustmentByCode(adjustments, props.adjustmentCode);

    const onDiscardConfirmation = useCallback(() => {
        // Invalidate and refetch the previously applied dataset (the one that may contain unsaved edits)
        const previousParams: GetAmountsEntriesParams = {
            CaseCode: datasetCase?.code ?? props.case,
            EntityCode: datasetEntity?.code ?? props.entity,
            Period: datasetYear?.periodYear?.toString() ?? props.period,
            AdjustmentCode: datasetAdjustment?.adjustmentCode ?? props.adjustmentCode,
            Jurisdiction: datasetJurisdiction?.code ?? props.jurisdiction,
        };
        queryClient.invalidateQueries({ queryKey: getGetAmountsEntriesQueryKey(previousParams) });
        refetchAmountEntries();

        if (dialogState.isFilterReset) {
            // Perform the reset without reopening confirmation: set defaults and apply
            const defaults = {
                adjustment: findIAdjustmentByCode(adjustments, props.adjustmentCode),
                calendarPeriod: findICalendarPeriodByCode(years, props.period),
                case: findICaseByCode(cases, props.case),
                entity: findIEntityByCode(entities, props.entity),
                jurisdiction: defaultJurisdiction,
            };
            updateDialogState({
                type: EnterAmountsModalActionActionType.FiltersUpdated,
                filterSelections: {
                    amountsOnly: false,
                    applyButtonDisabled: true,
                    combineDebitCreditColumns: false,
                    showAllAccounts: !props.isInputRow,
                    currentFilterSelections: defaults,
                    datasetFilterSelections: defaults,
                } as Partial<IEnterAmountsDialogState>,
            });
            updateDialogState({ type: EnterAmountsModalActionActionType.FiltersApplied });
        } else {
            updateDialogState({ type: EnterAmountsModalActionActionType.FiltersApplied });
        }
    }, [
        queryClient,
        datasetCase,
        datasetEntity,
        datasetJurisdiction,
        datasetYear,
        datasetAdjustment,
        props.case,
        props.entity,
        props.period,
        props.adjustmentCode,
        props.jurisdiction,
        refetchAmountEntries,
        dialogState.isFilterReset,
        adjustments,
        years,
        cases,
        entities,
        jurisdictions,
        defaultJurisdiction,
        updateDialogState,
    ]);

    return {
        accountAmountRows: dialogState.showAllAccounts ? amountsEntriesResponse?.accounts : filteredAccountAmountRows,
        cases,
        currentlyDisplayedCase: dialogState.currentFilterSelections?.case ?? datasetCase,
        datasetCase,
        entities,
        currentlyDisplayedEntity: dialogState.currentFilterSelections?.entity ?? datasetEntity,
        datasetEntity,
        jurisdictions,
        currentlyDisplayedJurisdiction: dialogState.currentFilterSelections?.jurisdiction ?? datasetJurisdiction,
        datasetJurisdiction,
        years,
        currentlyDisplayedYear: dialogState.currentFilterSelections?.calendarPeriod ?? datasetYear,
        datasetYear,
        adjustments,
        currentlyDisplayedAdjustment: dialogState.currentFilterSelections?.adjustment ?? datasetAdjustment,
        datasetAdjustment,
        saveAmounts: saveAmounts,
        updateDialogState,
        isLoading: isLoading,
        saveButtonIsDisabled: dialogState.saveButtonIsDisabled || isSaveAmountsLoading,
        changeTrackingData: dialogState.changeTrackingData,
        onFilterUpdate,
        onFilterReset,
        onFilterApply,
        dispatchFiltersUIState,
        amountsOnly: dialogState.amountsOnly,
        combineDebitCreditColumns: dialogState.combineDebitCreditColumns,
        showAllAccounts: dialogState.showAllAccounts,
        loadStateChangeEvent: loadStateChangeEvent,
        journalEntryGridProps: dialogState.journalEntryGridProps,
        journalEntryGridRef: journalEntryGridRef,
        applyButtonIsDisabled: dialogState.applyButtonDisabled,
        currentFilterSelections: dialogState.currentFilterSelections,
        datasetFilterSelections: dialogState.datasetFilterSelections,
        journalEntryGridPopupMenuDismissHack: getSyncfusionPopupMenuDismissHack(journalEntryGridRef, journalEntryGridFilterMenuRef),
        showConfirmationDialog: dialogState.showConfirmationDialog,
        confirmationDialogType: dialogState.confirmationDialogType,
        isFilterReset: dialogState.isFilterReset,
        isDirty: dialogState.isDirty,
        refetchAmounts: refetchAmountEntries,
        onDiscardConfirmation,
        isFilteredEnterAmountsEnabled,
        pendingJournalEntryArgs: dialogState.pendingJournalEntryArgs,
    } as IUseEnterAmountsDataHook;
};
export interface IEnterAmountsDialogFiltersUIState {
    applyButtonDisabled: boolean;
    amountsOnly: boolean;
    combineDebitCreditColumns: boolean;
    showAllAccounts: boolean;
    currentFilterSelections: IEnterAmountsDialogDatasetFilterSelections | null;
}
export interface IEnterAmountsDialogDatasetFilterSelections {
    adjustment?: IAdjustment;
    calendarPeriod?: ICalendarPeriod;
    case?: ICase;
    entity?: IEntity;
    jurisdiction?: IJurisdiction;
}
